import {
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
} from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import cn from 'classnames'
import React, { useEffect, useState } from 'react'
import config from '../../config.json'

import ErrorNote from '../common/util/ErrorNote'
import MainButton from '../common/util/input/MainButton'
import SuccessNote from '../common/util/SuccessNote'
import { FormLabel } from '../inputs/wrappers/FormElement'
import LoadingIndicator from '../loadingindicator'
import { useSharedState } from '../waxplorer/Waxplorer'
import { ImageUploadButton } from '../imageupload/ImageUploadButton'
import InputField from '../common/util/InputField'
import { checkCID, ipfsToCID } from '../helpers/Validators'

export const CreateBanner = () => {
    const [ipfs, setIPFS] = useState()
    const [url, setURL] = useState()
    const [startTime, setStartTime] = useState()
    const [editStartTime, setEditStartTime] = useState()
    const [endTime, setEndTime] = useState()
    const [editEndTime, setEditEndTime] = useState()
    const [success, setSuccess] = useState(false)
    const [market, setMarket] = useState('both')
    const [isLoading, setIsLoading] = useState(false)

    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [error, setError] = useState()

    const dismissError = () => {
        setError(null)
    }

    const dismissMessage = () => {
        setSuccess(false)
    }

    const changeIPFS = (e) => {
        const val = e.target.value
        setIPFS(val)
    }

    const changeURL = (e) => {
        const val = e.target.value
        setURL(val)
    }

    const convertDateToUnix = (date) => {
        return parseInt(date.getTime() / 1000).toFixed(0)
    }

    const createBanner = async () => {
        setIsLoading(true)
        if (
            activeUser &&
            activeUser['accountName'] &&
            [
                'frcqu.wam',
                't1.5c.wam',
                'tgz5k.wam',
                'scfay.wam',
                'nonoswax.gm',
            ].includes(activeUser['accountName'])
        ) {
            try {
                const result = await activeUser.session.transact(
                    {
                        actions: [
                            {
                                account: 'nft.hive',
                                name: 'execute',
                                authorization: [
                                    {
                                        actor: userName,
                                        permission: activeUser.requestPermission
                                            ? activeUser.requestPermission
                                            : 'active',
                                    },
                                ],
                                data: {
                                    signer: userName,
                                    action: JSON.stringify({
                                        name: 'create-banner',
                                        data: {
                                            ipfs: ipfs,
                                            url: url,
                                            startTime: startTime,
                                            endTime: endTime,
                                            market: market,
                                        },
                                    }),
                                },
                            },
                        ],
                    },
                    {
                        expireSeconds: 300,
                        blocksBehind: 0,
                    },
                )
                if (result?.response?.transaction_id) {
                    setSuccess(true)
                } else {
                    setError(result.status)
                }
            } catch (e) {
                setError(e.message)
            }
        }
        setIsLoading(false)
    }

    useEffect(() => {
        if (startTime > 0) {
            setEditStartTime(new Date(startTime * 1000))
        }
        if (endTime > 0) {
            setEditEndTime(new Date(endTime * 1000))
        }
    }, [startTime, endTime])

    return (
        <div
            className={
                'm-4 p-4 border bg-zinc-300 dark:!bg-paper rounded-md shadow-xl h-full border-paperl'
            }
        >
            <div className={'font-bold text-xl'}>Add New Banner</div>
            <div className={'grid grid-cols-1'}>
                <div className={'w-full mb-4'}>
                    <FormControl>
                        <FormLabel>Preview</FormLabel>
                        <a
                            href={url}
                            target="_blank"
                            className={cn(
                                'w-0 mb-4 md:mb-0 lg:w-auto lg:px-5 w-auto md:h-28 1xl:h-28 2xl:h-40',
                            )}
                            rel="noreferrer"
                        >
                            {' '}
                            {<img src={config.ipfs + ipfs} />}
                        </a>
                    </FormControl>
                </div>

                <div className={'w-full mb-4'}>
                    <FormControl>
                        <FormLabel>Publication Dates</FormLabel>
                        <div>
                            <div className={'mr-2 grid grid-cols-2 w-120'}>
                                <div className={'m-1'}>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDateFns}
                                    >
                                        <DateTimePicker
                                            label="Start Date and Time (Your local timezone)"
                                            value={editStartTime}
                                            onChange={(e) => {
                                                setStartTime(
                                                    convertDateToUnix(
                                                        new Date(
                                                            e.setSeconds(0),
                                                        ),
                                                    ),
                                                )
                                            }}
                                            ampm={false}
                                            minDateTime={new Date()}
                                            maxDateTime={editEndTime}
                                            renderInput={(params) => (
                                                <TextField
                                                    size="small"
                                                    {...params}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <div className={'m-1'}>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDateFns}
                                    >
                                        <DateTimePicker
                                            label="Start Date and Time (Your local timezone)"
                                            value={editEndTime}
                                            onChange={(e) => {
                                                setEndTime(
                                                    convertDateToUnix(
                                                        new Date(
                                                            e.setSeconds(0),
                                                        ),
                                                    ),
                                                )
                                            }}
                                            ampm={false}
                                            minDateTime={editStartTime}
                                            renderInput={(params) => (
                                                <TextField
                                                    size="small"
                                                    {...params}
                                                />
                                            )}
                                            validationError={
                                                editEndTime <= editStartTime
                                            }
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>
                        </div>
                    </FormControl>
                </div>
                <div className={'w-full mb-4'}>
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            Input
                        </FormLabel>
                        <div className={'grid grid-cols-2'}>
                            <div className={'m-1'}>
                                <TextField
                                    size="small"
                                    value={url}
                                    onChange={changeURL}
                                    name="URL"
                                    placeholder="URL"
                                    required
                                    label="URL"
                                />
                            </div>
                        </div>
                    </FormControl>
                </div>
                <div className={'w-full mb-4'}>
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            IPFS Hash
                        </FormLabel>
                        <div className={'grid grid-cols-2 mt-4'}>
                            <InputField
                                className="mr-2 w-full text-sm"
                                value={ipfs}
                                size={'lg'}
                                isDisabled={isLoading}
                                onChange={(e) => {
                                    let hash = e.target.value

                                    if (checkCID(hash)) {
                                        hash = ipfsToCID(hash)
                                    }

                                    setIPFS(hash)
                                }}
                            />
                            <div className={'mx-1'}>
                                <ImageUploadButton
                                    setIpfsHash={setIPFS}
                                    setError={setError}
                                    setIsUploading={setIsLoading}
                                    disabled={isLoading ? 'disabled' : ''}
                                />
                            </div>
                        </div>
                    </FormControl>
                </div>
                <div className={'w-full mb-4'}>
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            Market
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue={'both'}
                            name="radio-buttons-group"
                            onChange={(e) => setMarket(e.target.value)}
                        >
                            <FormControlLabel
                                value="both"
                                control={<Radio />}
                                label="Both Markets"
                            />
                            <FormControlLabel
                                value="nfthive"
                                control={<Radio />}
                                label="Only NftHive"
                            />
                            <FormControlLabel
                                value="waxmarketcap"
                                control={<Radio />}
                                label="Only WaxMarketCap"
                            />
                        </RadioGroup>
                    </FormControl>
                </div>
                <div className={'h-20'}>
                    {error ? (
                        <div className={'relative'}>
                            {' '}
                            <ErrorNote error={error} onClick={dismissError} />
                        </div>
                    ) : (
                        ''
                    )}
                    {success ? (
                        <div className={'relative'}>
                            <SuccessNote
                                error={'Banner created!'}
                                onClick={dismissMessage}
                            />
                        </div>
                    ) : (
                        ''
                    )}
                    {isLoading ? <LoadingIndicator /> : ''}
                    {!error && !success && !isLoading ? (
                        <div className={'w-40'}>
                            <MainButton onClick={createBanner}>Save</MainButton>
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        </div>
    )
}
